import { useEffect, useMemo, useState } from "react";
import { useDataProvider } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { type StoplossClaim, type ClaimItem, type Reimbursement, ResourceName } from "@/types";
import { type ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate, formatLongDate, DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { SectionHeader } from "../SectionHeader";
import { getProps } from "@/utils/helpers/columnsHelper";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ClaimNoteCell } from "./ClaimNoteCell";
import { paymentTypeLabel } from "../utils/paymentTypeLabel";
import { reimbursementDate } from "../utils/reportingPeriod";

// ==================== TYPES ====================

interface ClaimsDetailTableProps {
  /** Filtered stop-loss claims for breached claimants */
  claims: StoplossClaim[];
  loading: boolean;
  /**
   * claimant_id_ref -> claimant_name over the FULL fetched claimant set.
   *
   * Deliberately not derived from the Summary rows. #6828 made the Summary population
   * (spend-vs-deductible) independent of this table's (claims created in the period),
   * so a Summary-derived map is no longer total here and rendered below-deductible
   * claimants as "Unknown" (review thread 27746).
   */
  claimantNameById: Map<string, string>;
  isExpanded: boolean;
  onToggle: () => void;
}

// ==================== CONSTANTS ====================

const MAX_VISIBLE_ITEMS = 10;
const MAX_VISIBLE_REIMBURSEMENTS = 50;

// ==================== HELPERS ====================

const getColumnLabel = (key: string) => getProps(key, ResourceName.STOPLOSS_CLAIMS).label;

// ==================== COMPACT EXPAND ROW ====================

const ClaimItemsExpandRow = ({ claim }: { claim: StoplossClaim }) => {
  const dataProvider = useDataProvider();
  const [items, setItems] = useState<ClaimItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dataProvider
      .getList(ResourceName.CLAIM_ITEMS, {
        filter: { stoploss_claim_id_ref: claim.id },
        pagination: { page: 1, perPage: MAX_VISIBLE_ITEMS + 1 },
        sort: { field: "paid_date", order: "DESC" },
      })
      .then(({ data }: { data: ClaimItem[] }) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [claim.id, dataProvider]);

  if (loading) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        Loading claim items...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error" sx={{ py: 1 }}>
        Failed to load claim items
      </Typography>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        No claim items
      </Typography>
    );
  }

  const hasMore = items.length > MAX_VISIBLE_ITEMS;
  const visibleItems = hasMore ? items.slice(0, MAX_VISIBLE_ITEMS) : items;

  return (
    <Box>
      <Stack divider={<Divider sx={{ borderStyle: "dashed" }} />}>
        {visibleItems.map((item) => (
          <Box
            key={item.id}
            component={RouterLink}
            to={`/claim-items/${item.id}/show`}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 0.75,
              textDecoration: "none",
              color: "inherit",
              "&:hover": { opacity: 0.7 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                {item.provider || "Unknown provider"}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatLongDate(item.date_of_service)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" fontWeight={500}>
                {item.paid_amount ? formatUSD(item.paid_amount) : "—"}
              </Typography>
              <StatusChip
                label={formatToTitleCase(item.claim_status || "—")}
                color={getStatusColor(item.claim_status) ?? ("default" as ColorType)}
                size={UIComponentSize.Small}
              />
            </Box>
          </Box>
        ))}
      </Stack>
      {hasMore && (
        <Box sx={{ pt: 0.75 }}>
          <Typography
            component={RouterLink}
            to={`/stoploss-claims/${claim.id}/show`}
            variant="caption"
            color="primary"
            sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            View all claim items &rarr;
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ==================== REIMBURSEMENTS EXPAND ROW ====================

/**
 * Per-claim reimbursements sub-table (#5359 — the in-app twin of the #5310 export
 * reimbursement tab). Columns: Reimbursement Date, Amount, Type, Aegle Accel Funded Date.
 *
 * Data is fetched with `dataProvider.getList` inside a `useEffect` guarded by a `cancelled`
 * flag (NOT `useGetList` with a `= []` default, which mints a fresh array ref every render
 * and loops) — mirroring `ClaimItemsExpandRow`.
 *
 * The Aegle Accel funded date is claim-grain (per US 5330, where `aura_disbursed_at` is defined): it lives on the claim
 * itself (`aura_disbursed_at`), so every reimbursement row under a claim shows the same value.
 */
const ClaimReimbursementsExpandRow = ({ claim }: { claim: StoplossClaim }) => {
  const dataProvider = useDataProvider();
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dataProvider
      .getList(ResourceName.REIMBURSEMENTS, {
        filter: { stoploss_claim_id_ref: claim.id },
        pagination: { page: 1, perPage: MAX_VISIBLE_REIMBURSEMENTS },
        sort: { field: "check_date", order: "DESC" },
        meta: {
          "X-Fields": "id,payment_method,payment_date,check_date,received_date,reimbursement_amount,eft,check_number",
        },
      })
      .then(({ data, total }: { data: Reimbursement[]; total?: number }) => {
        if (!cancelled) {
          setReimbursements(data);
          setTotal(total ?? data.length);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [claim.id, dataProvider]);

  // Claim-grain funded date — same for every reimbursement under this claim.
  const fundedDate = claim.aura_disbursed_at ? formatDate(claim.aura_disbursed_at, DATE_FORMATS.DISPLAY) : "—";

  if (loading) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        Loading reimbursements...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error" sx={{ py: 1 }}>
        Failed to load reimbursements
      </Typography>
    );
  }

  if (reimbursements.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 1 }}>
        No reimbursements
      </Typography>
    );
  }

  // Re-sort by the date we actually display (`reimbursementDate`, #7560) desc. The BE sorts
  // by check_date (the indexed/sortable column), which can diverge from the authoritative
  // payment_date — sorting here keeps the visible order consistent with the visible dates.
  const sorted = [...reimbursements].sort((a, b) =>
    (reimbursementDate(b) ?? "").localeCompare(reimbursementDate(a) ?? "")
  );

  // Labeled grid so each value is self-explanatory (#5359 review feedback). The Funded
  // column is claim-grain (same value every row; aura_disbursed_at per US 5330).
  const cols = "96px 92px 1fr 104px";
  const headerSx = { textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.6rem" } as const;
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", gap: 1.5, pb: 0.5 }}>
        <Typography variant="caption" color="text.disabled" sx={headerSx}>
          Date
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={headerSx}>
          Type
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ ...headerSx, textAlign: "right" }}>
          Amount
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ ...headerSx, textAlign: "right" }}>
          Funded
        </Typography>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: "dashed" }} />}>
        {sorted.map((r) => {
          const reimbDate = reimbursementDate(r);
          return (
            <Box
              key={r.id}
              component={RouterLink}
              to={`/reimbursements/${r.id}/show`}
              sx={{
                display: "grid",
                gridTemplateColumns: cols,
                alignItems: "center",
                gap: 1.5,
                py: 0.75,
                textDecoration: "none",
                color: "inherit",
                "&:hover": { opacity: 0.7 },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {reimbDate ? formatDate(reimbDate, DATE_FORMATS.DISPLAY) : "—"}
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <StatusChip label={paymentTypeLabel(r)} color="default" size={UIComponentSize.Small} />
              </Box>
              <Typography
                variant="caption"
                fontWeight={500}
                sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
              >
                {r.reimbursement_amount !== null && r.reimbursement_amount !== undefined
                  ? formatUSD(r.reimbursement_amount)
                  : "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                {fundedDate}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {total > reimbursements.length && (
        <Typography variant="caption" color="text.disabled" sx={{ pt: 0.75, display: "block" }}>
          Showing {reimbursements.length} of {total}
        </Typography>
      )}
    </Box>
  );
};

// ==================== COMBINED EXPAND ROW ====================

const SubSectionLabel = ({ children }: { children: string }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    fontWeight={600}
    sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}
  >
    {children}
  </Typography>
);

/**
 * Expanded detail for a single claim row: the existing claim-items list plus the
 * new per-claim reimbursements sub-table (#5359), each under its own labeled section.
 */
const ClaimDetailExpandRow = ({ claim }: { claim: StoplossClaim }) => (
  <Stack spacing={1.5}>
    <Box>
      <Box sx={{ pb: 0.5 }}>
        <SubSectionLabel>Claim Items</SubSectionLabel>
      </Box>
      <ClaimItemsExpandRow claim={claim} />
    </Box>
    <Divider />
    <Box>
      <Box sx={{ pb: 0.5 }}>
        <SubSectionLabel>Reimbursements</SubSectionLabel>
      </Box>
      <ClaimReimbursementsExpandRow claim={claim} />
    </Box>
  </Stack>
);

// ==================== MAIN COMPONENT ====================

export const ClaimsDetailTable = ({
  claims,
  loading,
  claimantNameById,
  isExpanded,
  onToggle,
}: ClaimsDetailTableProps) => {
  const { canEdit: canEditClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  const columns: CompactTableColumn<StoplossClaim>[] = useMemo(
    () => [
      {
        key: "claimant",
        label: getColumnLabel("claimant_id_ref"),
        width: "1.2fr",
        render: (claim) => {
          const name = claimantNameById.get(String(claim.claimant_id_ref));
          // The link is unconditional. A name can still be missing — a claim whose
          // claimant_id_ref is outside the fetched claimant page (the truncation
          // condition the hook detects) — but the row always holds the id, and that id
          // is what makes an unnamed claimant recoverable. Dropping the link left the
          // user with "Unknown" and no way to find out who (review thread 27746).
          return (
            <RouterLink
              to={`/claimants/${claim.claimant_id_ref}/show`}
              onClick={(e) => e.stopPropagation()}
              style={{ textDecoration: "none" }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                  fontWeight: name ? 500 : 400,
                  fontStyle: name ? "normal" : "italic",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                {name || "Unnamed claimant"}
              </Typography>
            </RouterLink>
          );
        },
      },
      {
        key: "type",
        label: getColumnLabel("stoploss_claim_type"),
        width: "100px",
        render: (claim) => (
          <Typography variant="body2" sx={{ textTransform: "uppercase", color: "text.secondary", fontSize: "0.8rem" }}>
            {claim.stoploss_claim_type}
          </Typography>
        ),
      },
      {
        key: "status",
        label: getColumnLabel("claim_status"),
        width: "120px",
        render: (claim) => {
          const value = claim.claim_status;
          const label = value ? formatToTitleCase(value) : "Unknown";
          const color = getStatusColor(value) ?? ("default" as ColorType);
          return <StatusChip label={label} color={color} />;
        },
      },
      {
        // Scopes the reporting period (#6823). formatDate resolves in the reviewer's
        // local calendar, the same frame the period bounds are compared in, so the
        // date shown is the date that put this row in the period.
        key: "created_datetime",
        label: getColumnLabel("created_datetime"),
        width: "100px",
        render: (claim) => (
          <Typography variant="body2" color="text.secondary">
            {claim.created_datetime ? formatDate(claim.created_datetime, DATE_FORMATS.DISPLAY) : "—"}
          </Typography>
        ),
      },
      {
        key: "submitted_date",
        label: getColumnLabel("submitted_date"),
        width: "100px",
        render: (claim) => (
          <Typography variant="body2" color="text.secondary">
            {claim.submitted_date ? formatDate(claim.submitted_date, DATE_FORMATS.DISPLAY) : "—"}
          </Typography>
        ),
      },
      {
        key: "first_service_date",
        label: getColumnLabel("first_service_date"),
        width: "100px",
        render: (claim) => (
          <Typography variant="body2" color="text.secondary">
            {claim.first_service_date ? formatDate(claim.first_service_date, DATE_FORMATS.DISPLAY) : "—"}
          </Typography>
        ),
      },
      {
        key: "last_service_date",
        label: getColumnLabel("last_service_date"),
        width: "100px",
        render: (claim) => (
          <Typography variant="body2" color="text.secondary">
            {claim.last_service_date ? formatDate(claim.last_service_date, DATE_FORMATS.DISPLAY) : "—"}
          </Typography>
        ),
      },
      {
        key: "total_billed_amount",
        label: getColumnLabel("total_billed_amount"),
        width: "110px",
        align: "right",
        render: (claim) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {claim.total_billed_amount !== null && claim.total_billed_amount !== undefined
              ? formatUSD(claim.total_billed_amount)
              : "—"}
          </Typography>
        ),
      },
      {
        key: "total_paid_amount",
        label: getColumnLabel("total_paid_amount"),
        width: "110px",
        align: "right",
        render: (claim) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {claim.total_paid_amount !== null && claim.total_paid_amount !== undefined
              ? formatUSD(claim.total_paid_amount)
              : "—"}
          </Typography>
        ),
      },
      {
        key: "notes",
        label: getColumnLabel("notes"),
        width: "1fr",
        render: (claim) => <ClaimNoteCell claim={claim} canEdit={canEditClaims} />,
      },
    ],
    [claimantNameById, canEditClaims]
  );

  return (
    <SectionHeader
      title="Claims Detail"
      // Claim-grain and no longer gated on the Summary's breached-deductible
      // population (#6823/#6828) — every claim created in the period is listed.
      subtitle="Stop-loss claims created in the selected period"
      count={!loading ? claims.length : undefined}
      collapsible
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <CompactTable
          data={claims}
          columns={columns}
          rowsPerPage={10}
          emptyMessage="No stop-loss claims found for breached claimants in this policy and date range."
          expandRow={(claim) => <ClaimDetailExpandRow claim={claim} />}
        />
      )}
    </SectionHeader>
  );
};
